/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  Subject,
  distinctUntilChanged,
  filter,
  map,
  merge,
} from 'rxjs';

import { SidebarOpenConfigInterface } from './sidebar-open-config.interface';
import { SidebarContainerInterface } from './sidebar-container.interface';

@Injectable({
  providedIn: 'root',
})
export class SidebarService {
  private readonly _openTriggered: Subject<
    SidebarOpenConfigInterface<any, any>
  > = new Subject<SidebarOpenConfigInterface<any, any>>();
  private readonly _closeTriggered: Subject<string> = new Subject<string>();
  private readonly stateChangeMap: {
    [k: string]: BehaviorSubject<boolean>;
  } = {};

  readonly openTriggered$: Observable<SidebarOpenConfigInterface<any, any>>;
  readonly closeTriggered$: Observable<string>;

  constructor() {
    this.openTriggered$ = this._openTriggered.asObservable();
    this.closeTriggered$ = this._closeTriggered.asObservable();
  }

  stateChangeForSidebarId(sidebarId: string): Observable<boolean> {
    if (!this.stateChangeMap[sidebarId]) {
      this.stateChangeMap[sidebarId] = new BehaviorSubject(false);

      merge(
        this.openTriggered$.pipe(
          filter(configEmitted => configEmitted.id === sidebarId),
          map(() => true),
        ),

        this.closeTriggered$.pipe(
          filter(idEmitted => idEmitted === sidebarId),
          map(() => false),
        ),
      )
        .pipe(distinctUntilChanged())
        .subscribe(value => this.stateChangeMap[sidebarId].next(value));
    }

    return this.stateChangeMap[sidebarId].asObservable();
  }

  stateForSidebarId(sidebarId: string): boolean {
    return this.stateChangeMap[sidebarId]?.getValue() || false;
  }

  open<
    ContainerInterface extends SidebarContainerInterface,
    InputDataInterface = any,
  >(
    config: SidebarOpenConfigInterface<ContainerInterface, InputDataInterface>,
  ): void {
    this._openTriggered.next(config);
  }

  close(sidebarId: string): void {
    this._closeTriggered.next(sidebarId);
  }
}
